// File: src/composables/utils.ts

/**
 * @fileoverview API Error Response Processing Composable
 *
 * Primary Purpose: Vue 3 composable for processing and extracting meaningful
 * error messages from API responses. This composable is designed to be used
 * directly in components for handling specific API call errors.
 *
 * Scope: Component-level error handling for user-facing error messages
 * Usage Pattern: Import and use in components like `import { processError } from '@/composables/utils'`
 *
 * Architecture: Uses MessageService for intelligent error processing:
 * 1. Tries error code mapping (preferred)
 * 2. Falls back to pattern matching (pragmatic)
 * 3. Extracts raw messages (last resort)
 * 4. Provides fallbacks for network/HTML errors
 *
 * ⚠️  KNOWN ISSUE: This file contains mixed concerns - it includes both error
 * handling logic (appropriate) and email validation (inappropriate). The email
 * validation should be moved to @/utils/validationUtils to avoid duplication.
 * See: https://gitlab.com/NETLINK360/cirrus/lantern-frontend/-/issues/188
 *
 * Related Files:
 * - src/services/MessageService.ts (error code/pattern mapping)
 * - src/utils/ErrorHandler.js (global Vue error handler that uses this composable)
 * - src/utils/validationUtils.js (where validation utilities should be centralized)
 */

import { MessageService } from '@/services/MessageService'

interface ErrorResponse {
    data: unknown
    status: number
}

interface ErrorWithResponse {
    response?: ErrorResponse
    request?: unknown
    message?: string
    config?: { url?: string }
}

/**
 * Parses various error response structures from an API and extracts a meaningful error message.
 * Allows specifying a default message to use as a fallback.
 */
export const processError = (
    error: ErrorWithResponse | Error,
    defaultMessage: string = 'An error occurred. Please try again.'
): string => {
    const err = error as ErrorWithResponse

    if (err.response) {
        return handleResponseError(err.response, defaultMessage, err.config?.url)
    }
    if (err.request) {
        return 'No response from server. Please check your internet connection.'
    }
    if (err.message) {
        return err.message
    }
    return defaultMessage
}

/**
 * Handles error response data and extracts meaningful error message
 * Uses MessageService for intelligent error processing
 */
function handleResponseError(
    response: ErrorResponse,
    defaultMessage: string,
    url?: string
): string {
    const { data, status } = response

    // Handle string data (HTML or text responses)
    if (typeof data === 'string' && data.trim()) {
        return handleStringErrorData(data.trim(), status, url)
    }

    // Try MessageService for structured error processing (code mapping + pattern matching)
    const processedMessage = MessageService.processError(data)
    if (processedMessage) return processedMessage

    // Handle top-level error arrays without inventing numeric "field" labels.
    const arrayErrors = extractArrayErrors(data)
    if (arrayErrors) return arrayErrors

    // Handle field-level validation errors (e.g., {field1: ['Error 1'], field2: 'Error 2'})
    const fieldErrors = extractFieldErrors(data)
    if (fieldErrors) return fieldErrors

    // Prefer the caller-provided default over a generic status-based message
    return defaultMessage
}

/**
 * Handles string-type error data (HTML or plain text)
 */
function handleStringErrorData(trimmedData: string, status: number, url?: string): string {
    // Check if it's an HTML response
    if (isHtmlResponse(trimmedData)) {
        return getHtmlErrorMessage(status, url)
    }

    // If it's a plain text error message (not HTML), use it
    if (trimmedData.length < 500 && !trimmedData.includes('<')) {
        return trimmedData
    }

    // For other long strings or potential HTML fragments, use status-based message
    return getHtmlErrorMessage(status, url)
}

/**
 * Checks if the data is an HTML response
 */
function isHtmlResponse(data: string): boolean {
    return (
        data.startsWith('<!DOCTYPE') ||
        data.startsWith('<html') ||
        (data.includes('<title>') && data.includes('</title>'))
    )
}

/**
 * Generates user-friendly error messages for HTML error responses
 */
function getHtmlErrorMessage(status: number, url: string = ''): string {
    const endpoint = url ? ` (${url.split('/').pop()})` : ''

    switch (status) {
        case 404:
            return `API endpoint not found${endpoint}. This feature may not be implemented yet.`
        case 500:
            return `Server error (${status}). Please try again later.`
        case 502:
            return `Bad gateway (${status}). The server is temporarily unavailable.`
        case 503:
            return `Service unavailable (${status}). Please try again later.`
        case 504:
            return `Gateway timeout (${status}). The request took too long.`
        default:
            return `Server error (${status}). Please contact support if this persists.`
    }
}

/**
 * Capitalizes the first letter of a string
 */
function capitalize(s: string): string {
    return s.charAt(0).toUpperCase() + s.slice(1)
}

/**
 * Extracts and formats field-level validation errors from a Django REST
 * Framework error response body.
 *
 * **Input shape expected**:
 * ```json
 * { "field_name": ["Error 1", "Error 2"], "other_field": "Single error" }
 * ```
 *
 * **Output format**:
 * Each field is rendered as one block: `"Field Name: Error 1\nError 2"`.
 * Multiple fields are joined with `\n`, producing a single string that can
 * be passed directly to `announce()`.
 * Example output:
 * ```
 * Field Name: Error 1
 * Error 2
 * Other Field: Single error
 * ```
 *
 * **Field label normalisation**: snake_case keys are converted to Title Case
 * (e.g. `email_address` → `"Email Address"`) via `capitalize` + `split('_')`.
 *
 * **Field value handling**:
 * - `string[]` → joined with `\n` (DRF multi-error arrays)
 * - `string`   → used as-is
 * - anything else → serialised with `JSON.stringify`
 *
 * **Returns `null`** (falls through to other processors) when:
 * - `data` is falsy or not a plain object
 * - The object contains standard scalar error fields (`detail`, `message`,
 *   `error`, or `code`) — those are handled upstream by `MessageService`
 * - The object has no enumerable entries
 *
 * @param data - The parsed response body from an Axios error (`error.response.data`).
 * @returns A formatted multi-line error string, or `null` if the data does
 *   not match the expected field-error shape.
 */
function extractFieldErrors(data: unknown): string | null {
    if (!isPlainObject(data)) {
        return null
    }

    const errorData = data

    // Skip if it has standard error fields (handled by MessageService)
    if (errorData.detail || errorData.message || errorData.error || errorData.code) {
        return null
    }

    // Check if this looks like field-level errors
    const entries = Object.entries(errorData)
    if (entries.length === 0) {
        return null
    }

    // Format field errors: "Field name: Error 1\nError 2\n\nField2: Error"
    const formattedErrors = entries.map(([key, value]) => {
        const fieldLabel = key.split('_').map(capitalize).join(' ')
        let errorMessage: string
        if (Array.isArray(value)) {
            errorMessage = value.join('\n')
        } else if (typeof value === 'string') {
            errorMessage = value
        } else {
            errorMessage = JSON.stringify(value)
        }
        return `${fieldLabel}: ${errorMessage}`
    })

    return formattedErrors.join('\n')
}

/**
 * Extracts top-level array errors into a readable multi-line message.
 */
function extractArrayErrors(data: unknown): string | null {
    if (!Array.isArray(data) || data.length === 0) {
        return null
    }

    const messages = data
        .map((value) => {
            if (typeof value === 'string') {
                const trimmedValue = value.trim()
                return trimmedValue || null
            }

            if (typeof value === 'number' || typeof value === 'boolean') {
                return String(value)
            }

            if (value && typeof value === 'object') {
                return MessageService.processError(value)
            }

            return null
        })
        .filter((value): value is string => Boolean(value))

    if (messages.length === 0) {
        return null
    }

    return messages.join('\n')
}

function isPlainObject(data: unknown): data is Record<string, unknown> {
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
        return false
    }

    const prototype = Object.getPrototypeOf(data)
    return prototype === Object.prototype || prototype === null
}
